

import { useState, useEffect } from "react";

// react-github-btn
import GitHubButton from "react-github-btn";

// @mui material components
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// @mui icons
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Custom styles for the Configurator
import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";

// Material Dashboard 2 React context
// import {
//   useMaterialUIController,
//   setOpenConfigurator,
//   setTransparentSidenav,
//   setWhiteSidenav,
//   setFixedNavbar,
//   setSidenavColor,
// } from "context";
import { useDispatch, useSelector } from "react-redux";
import {
  setCache,
  setDarkMode,
  setFixedNavbar,
  setOpenConfigurator, setSidenavColor,
  setTransparentSidenav,
  setWhiteSidenav
} from "../../actions";
import LocalStorage from "../../utils/LocalStorage";
import {useTranslation} from "react-i18next";

function Configurator() {

  const [disabled, setDisabled] = useState(false);
  const sidenavColors = ["primary", "dark", "info", "success", "warning", "error"];
  const darkMode = useSelector(state => state?.common?.darkMode,(prev, next) => prev === next)
  const dispatch = useDispatch()
  const fixedNavbar = useSelector(state => state?.common?.fixedNavbar,(prev, next) => prev === next)
  const sidenavColor = useSelector(state => state?.common?.sidenavColor,(prev, next) => prev === next)
  const transparentSidenav = useSelector(state => state?.common?.transparentSidenav,(prev, next) => prev === next)
  const openConfigurator = useSelector(state => state?.common?.openConfigurator,(prev, next) => prev === next)
  const whiteSidenav = useSelector(state => state?.common?.whiteSidenav,(prev, next) => prev === next)
  const cache = useSelector(state => state?.common?.cache,(prev, next) => prev === next)
  const languageSelect = useSelector(state => state?.common?.language,(prev, next) => prev === next)
  const {t, i18n} = useTranslation();
  // Use the useEffect hook to change the button state for the sidenav type based on window size.
  useEffect(() => {
    // A function that sets the disabled state of the buttons for the sidenav type.
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }

    // The event listener that's calling the handleDisabled function when resizing the window.
    window.addEventListener("resize", handleDisabled);

    // Call the handleDisabled function to set the state with the initial value.
    handleDisabled();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  useEffect(() => {
    // console.log('languageSelect',languageSelect)
    const setting = {}
    setting.sidenavColor = sidenavColor
    setting.transparentSidenav = transparentSidenav
    setting.whiteSidenav = whiteSidenav
    setting.fixedNavbar = fixedNavbar
    setting.darkMode = darkMode
    setting.cache = cache
    setting.language = languageSelect


    LocalStorage.setItem('setting',JSON.stringify(setting))


  }, [sidenavColor,fixedNavbar,darkMode,transparentSidenav,whiteSidenav,cache,languageSelect]);

  const handleCloseConfigurator = () => dispatch(setOpenConfigurator( false))
  const handleTransparentSidenav = () => {
    dispatch(setTransparentSidenav( true));
    dispatch(setWhiteSidenav (false));
  };
  const handleWhiteSidenav = () => {
    dispatch(setWhiteSidenav(true));
    dispatch(setTransparentSidenav(false))
  };
  const handleDarkSidenav = () => {
    dispatch(setWhiteSidenav(false))
    dispatch(setTransparentSidenav(false))
  };
  const handleFixedNavbar = () => dispatch(setFixedNavbar(!fixedNavbar));
  const handleDarkMode = () =>{

    dispatch(setDarkMode(!darkMode))
  }

  const handleCache = () => {
    dispatch(setCache(!cache))
  }

  // sidenav type buttons styles
  const sidenavTypeButtonsStyles = ({
    functions: { pxToRem },
    palette: { white, dark, background },
    borders: { borderWidth },
  }) => ({
    height: pxToRem(39),
    background: darkMode ? background.sidenav : white.main,
    color: darkMode ? white.main : dark.main,
    border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? background.sidenav : white.main,
      color: darkMode ? white.main : dark.main,
      border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,
    },
  });

  // sidenav type active button styles
  const sidenavTypeActiveButtonStyles = ({
    functions: { pxToRem, linearGradient },
    palette: { white, gradients, background },
  }) => ({
    height: pxToRem(39),
    background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
    color: darkMode ? background.sidenav : white.main,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
      color: darkMode ? background.sidenav : white.main,
    },
  });

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={4}
        pb={0.5}
        px={3}
      >
        <MDBox>
          <MDTypography variant="h5">{t('SettingBar.Fulfillment System Configurator')}</MDTypography>
          <MDTypography variant="body2" color="text">
            {t('SettingBar.See our dashboard options.')}
          </MDTypography>
        </MDBox>

        <Icon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: darkMode ? white.main : dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </MDBox>

      <Divider />

      <MDBox pt={0.5} pb={3} px={3}>
        <MDBox>
          <MDTypography variant="h6">{t('SettingBar.Sidenav Colors')}</MDTypography>

          <MDBox mb={0.5}>
            {sidenavColors.map((color) => (
              <IconButton
                key={color}
                sx={({
                  borders: { borderWidth },
                  palette: { white, dark, background },
                  transitions,
                }) => ({
                  width: "24px",
                  height: "24px",
                  padding: 0,
                  border: `${borderWidth[1]} solid ${darkMode ? background.sidenav : white.main}`,
                  borderColor: () => {
                    let borderColorValue = sidenavColor === color && dark.main;

                    if (darkMode && sidenavColor === color) {
                      borderColorValue = white.main;
                    }

                    return borderColorValue;
                  },
                  transition: transitions.create("border-color", {
                    easing: transitions.easing.sharp,
                    duration: transitions.duration.shorter,
                  }),
                  backgroundImage: ({ functions: { linearGradient }, palette: { gradients } }) =>
                    linearGradient(gradients[color].main, gradients[color].state),

                  "&:not(:last-child)": {
                    mr: 1,
                  },

                  "&:hover, &:focus, &:active": {
                    borderColor: darkMode ? white.main : dark.main,
                  },
                })}
                onClick={() => dispatch(setSidenavColor(color))}
              />
            ))}
          </MDBox>
        </MDBox>

        <MDBox mt={3} lineHeight={1}>
          <MDTypography variant="h6">{t('SettingBar.Sidenav Type')}</MDTypography>
          <MDTypography variant="button" color="text">
            {t('SettingBar.Choose between different sidenav types.')}
          </MDTypography>

          <MDBox
            sx={{
              display: "flex",
              mt: 2,
              mr: 1,
            }}
          >
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleDarkSidenav}
              disabled={disabled}
              fullWidth
              sx={
                !transparentSidenav && !whiteSidenav
                  ? sidenavTypeActiveButtonStyles
                  : sidenavTypeButtonsStyles
              }
            >
              {t('SettingBar.Dark')}
            </MDButton>
            <MDBox sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleTransparentSidenav}
                disabled={disabled}
                fullWidth
                sx={
                  transparentSidenav && !whiteSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                {t('SettingBar.Transparent')}
              </MDButton>
            </MDBox>
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleWhiteSidenav}
              disabled={disabled || darkMode}
              fullWidth
              sx={
                whiteSidenav && !transparentSidenav
                  ? sidenavTypeActiveButtonStyles
                  : sidenavTypeButtonsStyles
              }
            >
              {t('SettingBar.White')}
            </MDButton>
          </MDBox>
        </MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
          lineHeight={1}
        >
          <MDTypography variant="h6">{t('SettingBar.Navbar Fixed')}</MDTypography>

          <Switch checked={fixedNavbar} onChange={handleFixedNavbar} />
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
          <MDTypography variant="h6">{t('SettingBar.Light / Dark')}</MDTypography>

          <Switch checked={darkMode} onChange={handleDarkMode} />
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
          <MDTypography variant="h6">{t('SettingBar.Auto Cache / Manually Cache')} </MDTypography>

          <Switch checked={cache} onChange={handleCache} />
        </MDBox>
        <Divider />
        {/*<MDBox mt={3} mb={2}>*/}
        {/*  <MDButton*/}
        {/*    rel="noreferrer"*/}
        {/*    color={darkMode ? "light" : "dark"}*/}
        {/*    variant="outlined"*/}
        {/*    fullWidth*/}
        {/*  >*/}
        {/*    view documentation*/}
        {/*  </MDButton>*/}
        {/*</MDBox>*/}

        {/*<MDBox mt={2} textAlign="center">*/}
        {/*  <MDBox mb={0.5}>*/}
        {/*    <MDTypography variant="h6">Thank you for sharing!</MDTypography>*/}
        {/*  </MDBox>*/}

        {/*  <MDBox display="flex" justifyContent="center">*/}
        {/*    <MDBox mr={1.5}>*/}
        {/*      <MDButton*/}
        {/*          component={Link}*/}
        {/*          href={`//twitter.com/intent/tweet?text=Darwynn%20Fulfillment%20System%20made%20by%20%40Darwynn%20Ltd.%20%23darwynn%20%23fulfillment&url=https%3A%2F%2Fcsr.darwynnfulfillment.com`}*/}
        {/*          target="_blank"*/}
        {/*          rel="noreferrer"*/}
        {/*          color="dark"*/}
        {/*      >*/}
        {/*        <TwitterIcon />*/}
        {/*        &nbsp; Tweet*/}
        {/*      </MDButton>*/}
        {/*    </MDBox>*/}
        {/*    <MDButton*/}
        {/*      component={Link}*/}
        {/*      href="https://www.facebook.com/sharer/sharer.php?u=https://csr.darwynnfulfillment.com/authentication/sign-in"*/}
        {/*      target="_blank"*/}
        {/*      rel="noreferrer"*/}
        {/*      color="dark"*/}
        {/*    >*/}
        {/*      <FacebookIcon />*/}
        {/*      &nbsp; Share*/}
        {/*    </MDButton>*/}
        {/*  </MDBox>*/}
        {/*</MDBox>*/}
      </MDBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;
